import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type OptionRowColorStateConstantType,
    type OptionRowMinHeightConstantType,
    type OptionRowMeasuresConstantType,
    type OptionRowRadiusConstantType,
    type OptionRowColorsConstantType,
    type OptionRowInsetConstantType,
    type OptionRowDynamicStyleType,
    type OptionRowColorState,
    type OptionRowMeasures,
    type OptionRowVariant,
    type OptionRowColors,
    type OptionRowType
} from "./type";
import {
    getTypeColors
} from "../../helpers/theme/typeColors";
import type {
    Mutable
} from "../../types";
import {
    type StateColorKey,
    getFocusRingStyle,
    STATE_COLOR_KEYS
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getRowIconGap
} from "../../helpers/theme/control";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    webStyle
} from "../../utils";

export const OPTION_ROW_OVERLAY_KEYS: Record<OptionRowType, StateColorKey> = {
    danger: STATE_COLOR_KEYS.danger,
    neutral: "default"
};

export const OPTION_ROW_GAP_KEYS: Record<OptionRowVariant, keyof NCoreUIKit.Spaces> = {
    popover: "spacingSm",
    sheet: "spacingMd"
};

export const OPTION_ROW_ICON_COLOR_KEYS: Record<OptionRowColorState, Record<OptionRowType, keyof NCoreUIKit.IconContentColors>> = {
    disabled: {
        danger: "disabled",
        neutral: "disabled"
    },
    rest: {
        danger: "danger",
        neutral: "mid"
    },
    selected: {
        danger: "high",
        neutral: "high"
    }
};

export const OPTION_ROW_TEXT_COLOR_KEYS: Record<OptionRowColorState, Record<"description" | "shortcut" | "meta", keyof NCoreUIKit.TextContentColors>> = {
    disabled: {
        shortcut: "disabled",
        description: "low",
        meta: "disabled"
    },
    rest: {
        description: "mid",
        shortcut: "low",
        meta: "low"
    },
    selected: {
        description: "mid",
        shortcut: "mid",
        meta: "high"
    }
};

export const OPTION_ROW_LABEL_VARIANT: keyof NCoreUIKit.Typography = "bodyMediumSize";

export const OPTION_ROW_DESCRIPTION_VARIANT: keyof NCoreUIKit.Typography = "bodySmallSize";

export const OPTION_ROW_SHORTCUT_VARIANT: keyof NCoreUIKit.Typography = "labelSmallSize";

export const OPTION_ROW_META_VARIANT: keyof NCoreUIKit.Typography = "bodyMediumSize";

export const OPTION_ROW_SPACE_KEY = " ";

export const getOptionRowColorState = ({
    isSelectedToneVisible,
    isDisabled
}: OptionRowColorStateConstantType): OptionRowColorState => {
    if(isDisabled) {
        return "disabled";
    }

    return isSelectedToneVisible ? "selected" : "rest";
};

export const getOptionRowMinHeight = ({
    controlHeights,
    touchTargets,
    pointerType,
    variant
}: OptionRowMinHeightConstantType): number => {
    if(variant === "sheet") {
        return touchTargets.preferred;
    }

    if(pointerType === "coarse") {
        return Math.max(controlHeights.large, touchTargets.preferred);
    }

    return controlHeights.small;
};

export const getOptionRowMeasures = ({
    isDescriptionVisible,
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    variant,
    spaces
}: OptionRowMeasuresConstantType): OptionRowMeasures => {
    const labelLineHeight = typography.bodyMediumSize.lineHeight;

    const shortcutLineHeight = typography.labelSmallSize.lineHeight;

    const iconSize = iconSizes.small;

    const endGap = spaces.spacingLg - spaces.spacingSm;

    const minHeight = getOptionRowMinHeight({
        controlHeights,
        touchTargets,
        pointerType,
        variant
    });

    if(variant === "sheet") {
        return {
            paddingHorizontal: spaces.spacingLg - spaces.spacingXs,
            paddingVertical: spaces.spacingSm,
            gap: getRowIconGap({
                rowHeight: minHeight,
                controlHeights,
                spaces
            }),
            alignItems: "center",
            firstLineOffset: 0,
            shortcutOffset: 0,
            minHeight,
            iconSize,
            endGap
        };
    }

    if(pointerType === "coarse") {
        return {
            paddingVertical: isDescriptionVisible ? spaces.spacingSm : 0,
            paddingHorizontal: spaces.spacingSm,
            gap: getRowIconGap({
                rowHeight: minHeight,
                controlHeights,
                spaces
            }),
            alignItems: "center",
            firstLineOffset: 0,
            shortcutOffset: 0,
            minHeight,
            iconSize,
            endGap
        };
    }

    return {
        paddingVertical: isDescriptionVisible ? (controlHeights.small - labelLineHeight) / 2 : 0,
        shortcutOffset: isDescriptionVisible ? (labelLineHeight - shortcutLineHeight) / 2 : 0,
        firstLineOffset: isDescriptionVisible ? (labelLineHeight - iconSize) / 2 : 0,
        alignItems: isDescriptionVisible ? "flex-start" : "center",
        paddingHorizontal: spaces.spacingSm,
        gap: getRowIconGap({
            rowHeight: minHeight,
            controlHeights,
            spaces
        }),
        minHeight,
        iconSize,
        endGap
    };
};

export const getOptionRowInset = ({
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    iconSizes,
    variant,
    spaces
}: OptionRowInsetConstantType): number => {
    const {
        iconSize,
        gap
    } = getOptionRowMeasures({
        isDescriptionVisible: false,
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        variant,
        spaces
    });

    return iconSize + gap;
};

export const getOptionRowRadius = ({
    minHeight,
    radiuses,
    borders,
    variant,
    spaces
}: OptionRowRadiusConstantType): number => {
    if(variant === "sheet") {
        return Math.min(radiuses.actions, minHeight / 2);
    }

    return concentric({
        inset: spaces.spacingXs + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions,
        isCapsuleExempt: false
    });
};

export const getOptionRowColors = ({
    isSelectedToneVisible,
    isActiveToneVisible,
    isDisabled,
    isLayered,
    colors,
    type
}: OptionRowColorsConstantType): OptionRowColors => {
    const colorState = getOptionRowColorState({
        isSelectedToneVisible,
        isDisabled
    });

    const typeColors = getTypeColors({
        isSelected: isSelectedToneVisible,
        variant: "ghost",
        isDisabled,
        colors,
        type
    });

    const textKeys = OPTION_ROW_TEXT_COLOR_KEYS[colorState];

    const iconColorKey = OPTION_ROW_ICON_COLOR_KEYS[colorState][type];

    const currentColors: OptionRowColors = {
        overlayColor: isSelectedToneVisible ? "default" : OPTION_ROW_OVERLAY_KEYS[type],
        descriptionColor: colors.content.text[textKeys.description],
        shortcutColor: colors.content.text[textKeys.shortcut],
        metaColor: colors.content.text[textKeys.meta],
        containerColor: typeColors.containerColor,
        labelColor: typeColors.titleColor,
        checkColor: typeColors.iconColor,
        iconColor: typeColors.iconColor,
        iconColorKey
    };

    if(colorState === "disabled") {
        currentColors.metaColor = typeColors.iconColor;

        return currentColors;
    }

    if(colorState === "selected") {
        return currentColors;
    }

    if(isLayered) {
        currentColors.shortcutColor = colors.content.text.mid;
    }

    if(isActiveToneVisible) {
        currentColors.containerColor = colors.content.container.selected;
    }

    if(type === "neutral") {
        currentColors.iconColor = colors.content.icon[iconColorKey];

        currentColors.checkColor = colors.content.icon.high;
    }

    return currentColors;
};

const stylesheet = StyleSheet.create({
    shortcut: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0,
        ...webStyle({
            unicodeBidi: "plaintext"
        })
    },
    container: {
        flexDirection: "row",
        alignSelf: "stretch",
        ...webStyle({
            outlineStyle: "solid",
            userSelect: "none",
            outlineWidth: 0
        })
    },
    iconSlot: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    textColumn: {
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    leadingSlot: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "center",
        flexShrink: 0
    },
    trailing: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    meta: {
        fontVariant: [
            "tabular-nums"
        ],
        flexShrink: 0
    },
    label: {
        minWidth: 0,
        flex: 1
    }
});

export const useStyles = ({
    isFocusRingVisible,
    currentMeasures,
    currentColors,
    isMetaVisible,
    borderRadius,
    shapeConfigs,
    isDisabled,
    borders,
    colors,
    spaces,
    type
}: OptionRowDynamicStyleType) => {
    const styles = {
        container: {
            paddingHorizontal: currentMeasures.paddingHorizontal,
            paddingVertical: currentMeasures.paddingVertical,
            backgroundColor: currentColors.containerColor,
            alignItems: currentMeasures.alignItems,
            minHeight: currentMeasures.minHeight,
            gap: currentMeasures.gap,
            ...getCornerStyle({
                radius: borderRadius,
                shapeConfigs
            })
        } as Mutable<ViewStyle>,
        iconSlot: {
            marginTop: currentMeasures.firstLineOffset,
            height: currentMeasures.iconSize,
            width: currentMeasures.iconSize
        } as Mutable<ViewStyle>,
        shortcut: {
            marginTop: currentMeasures.shortcutOffset,
            paddingStart: currentMeasures.endGap
        } as Mutable<ViewStyle>,
        trailing: {
            marginTop: currentMeasures.firstLineOffset
        } as Mutable<ViewStyle>,
        textColumn: {
            gap: spaces.spacingXxs
        } as Mutable<ViewStyle>
    };

    if(!isMetaVisible) {
        styles.trailing.marginStart = currentMeasures.endGap;
    }

    Object.assign(styles.container, getFocusRingStyle({
        colorKey: OPTION_ROW_OVERLAY_KEYS[type],
        isFocusVisible: isFocusRingVisible,
        isInset: true,
        borders,
        colors
    }));

    if(isDisabled) {
        Object.assign(styles.container, webStyle({
            cursor: "not-allowed"
        }));
    }

    return styles;
};
export default stylesheet;
